import { memo, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { createSelector } from 'reselect';

import { InfoTooltip } from 'components/v3';

import { useStyles } from './ChatMessageMarkdownHtml.styles';
import { handleReactTagsFromString } from 'components/message/commonFunctions';
import styles from '../multistep/multi-step-message.scss';

const getPartialAnswerIdToMessageId = createSelector(
  state => state.chatMessages,
  chatMessages => {
    const map = new Map();

    chatMessages.forEach(msg => {
      if (msg.answer?.partialAnswerId) {
        // single answer
        map.set(msg.answer.partialAnswerId, msg.id);
      } else if (msg.widgetsToPartialAnswersIdMap) {
        // board answer: remember the reference for the partial answers in all widgets
        msg.widgetsToPartialAnswersIdMap.forEach(widget => {
          map.set(widget.partialAnswerId, msg.id);
        });
      }
    });

    return map;
  }
);

// Important: this is also used to retrieve the cursor element in the removeCursor function, do not use it elsewhere
const cursorStylesClass = styles.blinkingCursor;

// a special markdown HTML container for chat messages which supports running chat-specific actions
// when clicking special hyperlinks (e.g., asking a suggested question)
const ChatMessageMarkdownHtml = ({ html, scrollTo, withCursor = false }) => {
  const containerRef = useRef(null);
  const { classes } = useStyles();
  const { t } = useTranslation('veezoo');

  const username = useSelector(state => state.user.username);
  const partialAnswerIdToMessageId = useSelector(getPartialAnswerIdToMessageId);
  const dispatch = useDispatch();

  // Adds the cursor to the end of the last text node in the container
  // We do this instead of just appending the cursor to the container directly because the HTML may contain e.g. a paragraph (<p>...</p>) at the end,
  // which would lead to the cursor being put on a new line instead of where we expect new text to appear
  const addCursor = () => {
    if (containerRef.current) {
      // Find the last non-empty text node in the container
      const walker = document.createTreeWalker(containerRef.current, NodeFilter.SHOW_TEXT, null);
      let lastNode;

      while (walker.nextNode()) {
        // Skip empty text nodes
        if (/\S/.test(walker.currentNode.data)) {
          lastNode = walker.currentNode;
        }
      }

      // Append the cursor at the end of the last text node (by appending it to the parent node)
      if (lastNode) {
        // Create the cursor element (as a plain HTML element, we are operating on the DOM directly)
        const cursorSpan = document.createElement('span');
        cursorSpan.className = cursorStylesClass;
        cursorSpan.setAttribute('data-test', 'streamedTextCursor');
        lastNode.parentNode.appendChild(cursorSpan);
      }
    }
  };

  // Removes the cursor from the container
  const removeCursor = () => {
    if (containerRef.current) {
      const existingCursor = containerRef.current.getElementsByClassName(cursorStylesClass);

      if (existingCursor?.length > 0) {
        existingCursor[0].remove();
      }
    }
  };

  // logic to add cursor if withCursor is true
  useEffect(() => {
    // remove any existing cursor first
    removeCursor();

    if (!withCursor || !containerRef.current) return;

    // add new cursor
    addCursor();
  }, [html, withCursor]);

  const xveezoodataModification = {
    nodeName: 'xveezoodata',
    processModification: domNode => {
      // NOTE: Markdown conversion has converted all attributes to lowercase
      const partialAnswerId = domNode.attribs?.partialanswerid;
      const referencedMessageId = partialAnswerIdToMessageId.get(partialAnswerId);
      return (
        <InfoTooltip text={t('text-answer-message.datapoint-tooltip')} placement="top">
          <span className={classes.dataReference} onClick={() => scrollTo && scrollTo(referencedMessageId)}>
            {domNode.children[0].data}
          </span>
        </InfoTooltip>
      );
    }
  };

  const formattedDescription = handleReactTagsFromString(html, username, dispatch, t, [xveezoodataModification]);

  return (
    <div ref={containerRef} className={classes.root}>
      {formattedDescription}
    </div>
  );
};

export default memo(ChatMessageMarkdownHtml);
